import React, { useEffect, useState } from 'react';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { fetchWithRetry } from '../utils/tourUtils';
import { FUNCTION_NAMES } from '../constants';
import { API_BASE_URL } from '../config';

const LocationSelectorBase = ({
  clientId,
  projectId,
  lotId,
  buildingId,
  floorId,
  spaceId,
  onChange,
  hideFloorSpace = false,
  tourMap,
  forceProjectSelectionAlways = false, // ✅ new
}) => {
  const [projects, setProjects] = useState([]);
  const [lots, setLots] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [spaces, setSpaces] = useState([]);

  useEffect(() => {
    fetchWithRetry({
      url: `${API_BASE_URL}/api/post`,
      data: {
        function_name: FUNCTION_NAMES.PRISMA,
        operation: 'project.findMany',
        payload: {},
      },
      label: 'projects',
    }).then(setProjects);
  }, []); // ✅ Only once on mount

  useEffect(() => {
    if (!projectId) return;
    fetchWithRetry({
      url: `${API_BASE_URL}/api/post`,
      data: {
        function_name: FUNCTION_NAMES.PRISMA,
        operation: 'lot.findByProjectId',
        payload: { projectId },
      },
      label: 'lots',
    }).then(setLots);
  }, [projectId]);

  useEffect(() => {
    if (!projectId || !lotId) return;
    fetchWithRetry({
      url: `${API_BASE_URL}/api/post`,
      data: {
        function_name: FUNCTION_NAMES.PRISMA,
        operation: 'building.findByProjectAndLot',
        payload: { projectId, lotId },
      },
      label: 'buildings',
    }).then(setBuildings);
  }, [projectId, lotId]);

  useEffect(() => {
    if (!projectId || !lotId || !buildingId) return;
    fetchWithRetry({
      url: `${API_BASE_URL}/api/post`,
      data: {
        function_name: FUNCTION_NAMES.PRISMA,
        operation: 'floor.findByProjectLotBuilding',
        payload: { projectId, lotId, buildingId },
      },
      label: hideFloorSpace ? 'floors_with_spaces' : 'floors',
    }).then(result => {
      if (hideFloorSpace) {
        setFloors(result);
        const allSpaces = result.flatMap(f => f.spaces);
        setSpaces(allSpaces);
        onChange?.({
          projectId,
          lotId,
          buildingId,
          floorId,
          spaceId,
          floors: result,
          spaces: allSpaces,
        });
      } else {
        setFloors(result);
        onChange?.({
          projectId,
          lotId,
          buildingId,
          floorId,
          spaceId,
          floors: result,
          spaces: [],
        });
      }
    });
  }, [projectId, lotId, buildingId, hideFloorSpace]);

  useEffect(() => {
    if (!projectId || !lotId || !buildingId || !floorId || hideFloorSpace) return;
    fetchWithRetry({
      url: `${API_BASE_URL}/api/post`,
      data: {
        function_name: FUNCTION_NAMES.PRISMA,
        operation: 'space.findByProjectLotBuildingFloor',
        payload: { projectId, lotId, buildingId, floorId },
      },
      label: 'spaces',
    }).then(setSpaces);
  }, [projectId, lotId, buildingId, floorId, hideFloorSpace]);
  const handleSelect = (field, value) => {
    const updated = {
      projectId: field === 'projectId' ? value : projectId,
      lotId: field === 'lotId' ? value : field === 'projectId' ? '' : lotId,
      buildingId:
        field === 'buildingId'
          ? value
          : field === 'projectId' || field === 'lotId'
            ? ''
            : buildingId,
      floorId:
        field === 'floorId'
          ? value
          : field === 'projectId' || field === 'lotId' || field === 'buildingId'
            ? ''
            : floorId,
      spaceId: field === 'spaceId' ? value : '',
    };

    onChange?.({
      ...updated,
      floors, // <- catch floors for BuildingSVG
      spaces, // <- catch spaces for BuildingSVG
    });
  };

  return (
    <div className="space-y-4">
      {(!projectId || forceProjectSelectionAlways) && (
        <Select value={projectId} onValueChange={value => handleSelect('projectId', value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select Project" />
          </SelectTrigger>
          <SelectContent className="max-h-60">
            <ScrollArea className="h-full">
              {projects.map(p => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </ScrollArea>
          </SelectContent>
        </Select>
      )}

      <Select
        value={lotId}
        onValueChange={value => handleSelect('lotId', value)}
        disabled={!projectId}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select Lot" />
        </SelectTrigger>
        <SelectContent className="max-h-60">
          <ScrollArea className="h-full">
            {lots.map(lot => (
              <SelectItem key={lot.id} value={lot.id}>
                {lot.name}
              </SelectItem>
            ))}
          </ScrollArea>
        </SelectContent>
      </Select>

      <Select
        value={buildingId}
        onValueChange={value => handleSelect('buildingId', value)}
        disabled={!lotId}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select Building" />
        </SelectTrigger>
        <SelectContent className="max-h-60">
          <ScrollArea className="h-full">
            {buildings.map(building => (
              <SelectItem key={building.id} value={building.id}>
                {building.name}
              </SelectItem>
            ))}
          </ScrollArea>
        </SelectContent>
      </Select>

      {!hideFloorSpace && (
        <>
          <Select
            value={floorId}
            onValueChange={value => handleSelect('floorId', value)}
            disabled={!buildingId}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select Floor" />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              <ScrollArea className="h-full">
                {floors.map(floor => (
                  <SelectItem key={floor.id} value={floor.id}>
                    {floor.name}
                  </SelectItem>
                ))}
              </ScrollArea>
            </SelectContent>
          </Select>

          <Select
            value={spaceId}
            onValueChange={value => handleSelect('spaceId', value)}
            disabled={!floorId}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select Space" />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              <ScrollArea className="h-full">
                {spaces.map(space => (
                  <SelectItem key={space.id} value={space.id}>
                    {space.name}
                  </SelectItem>
                ))}
              </ScrollArea>
            </SelectContent>
          </Select>
        </>
      )}
    </div>
  );
};

export default LocationSelectorBase;
